import { isTransientHubFetchError } from "@/lib/api";
import { isDevStackRestartingFromFlags, type DevStackStatusSnapshot } from "@/lib/dev-stack-status";

export type DevRestartGateFlags = Partial<
  Record<"erp-api" | "erp-ui" | "erp-vendor-ui", boolean>
>;

/** Keep the authenticated shell when `/api/me` failed transiently but cached data exists (dev only). */
export function shouldKeepHubShellForDevRestart(options: {
  dev: boolean;
  hasCachedSession: boolean;
  hasMeData: boolean;
  meError: unknown;
  devRestartFlags: DevRestartGateFlags;
}): boolean {
  if (!options.dev) {
    return false;
  }
  const restarting = isDevStackRestartingFromFlags(options.devRestartFlags);
  if (options.hasCachedSession && restarting) {
    return true;
  }
  if (options.hasCachedSession && isTransientHubFetchError(options.meError)) {
    return true;
  }
  if (!options.hasMeData) {
    return false;
  }
  if (restarting) {
    return true;
  }
  return isTransientHubFetchError(options.meError);
}

/** Avoid the centered full-page spinner while the local stack is restarting (dev only). */
export function shouldSuppressFullPageMeLoading(options: {
  dev: boolean;
  mePending: boolean;
  hasCachedSession: boolean;
  devRestartFlags: DevRestartGateFlags;
}): boolean {
  if (!options.dev || !options.mePending) {
    return false;
  }
  if (isDevStackRestartingFromFlags(options.devRestartFlags)) {
    return true;
  }
  return options.hasCachedSession;
}

export function devRestartFlagsFromSnapshot(snapshot: DevStackStatusSnapshot): DevRestartGateFlags {
  const flags: DevRestartGateFlags = {};
  for (const key of ["erp-api", "erp-ui", "erp-vendor-ui"] as const) {
    if (snapshot[key]?.phase === "restarting") {
      flags[key] = true;
    }
  }
  return flags;
}
